import React, { useEffect, useState } from 'react';
import { SVGS } from '../../../../../assets/images';
import { Button } from '@mui/material';
import styles from './multiple-file-uploader.module.scss';
import { useField, useFormikContext } from 'formik';
import { useIntl } from 'react-intl';
import { Lx } from '../../../../../i18n/consts';
import { ALLOWED_EXTENSIONS, FileMetadata, getFileExtension, MAX_COST_FILES_COUNT } from '../../../../../features/cost-report/types';
import { IsValidExtension } from '../helpers';
import DownloadButton from '../../../../../components/shared/file-download-button';

interface Props {
    label: string;
    name: string;
    initialValues?: FileMetadata[];
    existingFilesFieldName?: string;
    onRemovedFiles?: (removedFiles: FileMetadata[]) => void;
    maxFilesCount?: number;
    validateExtension?: boolean;
    hint?: string;
}

type CommonFileItem = {
    source: 'server' | 'local';
    file: FileMetadata | File;
};

function isFileMetadata(file: File | FileMetadata): file is FileMetadata {
    return 'id' in file && 'storageName' in file && 'extension' in file;
}

const MultipleFileUploader = (props: Props) => {
    const [files, setFiles] = useState<CommonFileItem[]>(
        props.initialValues && props.initialValues.length > 0 
            ? props.initialValues.map((file) => ({ source: 'server', file }))
            : []);
    const { setFieldValue } = useFormikContext<Record<string, unknown>>();
    const [, meta] = useField(props.name);
    const intl = useIntl();
    const maxFilesCount = props.maxFilesCount ?? MAX_COST_FILES_COUNT; 
    const validateExtension = props.validateExtension ?? true;
    const acceptedExtensions = validateExtension ? '.' + ALLOWED_EXTENSIONS.join(',.') : '';

    const [removedFiles, setRemovedFiles] = useState<FileMetadata[]>(
        props.initialValues && props.initialValues.length > 0 
            ? props.initialValues.filter(
                (file) =>
                    !files.some(
                        (item) =>
                            item.source === 'server' && (item.file as FileMetadata).id === file.id
                    ))
            : []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (files && files.length + (e.target.files ? e.target.files.length : 0) > maxFilesCount) {
            return;
        }

        if (e.target.files) {
            const newFiles = Array.from(e.target.files);

            if (validateExtension && newFiles.every((file) => IsValidExtension(file))) {
                const newFileItems = newFiles.map((file) => ({
                    source: 'local' as const,
                    file,
                }));
                setFiles((prev) => [...prev, ...newFileItems]);
            }
        }
    };

    const removeFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    useEffect(() => {
        const uploadedFiles = files
            .map((file) => file.file)
            .filter((file): file is File => file instanceof File);
        setFieldValue(props.name, uploadedFiles, true);

        const currentExistingFiles = files
            .map((item) => item.file)
            .filter(isFileMetadata);

        if (props.existingFilesFieldName) {
            setFieldValue(props.existingFilesFieldName, currentExistingFiles, true);
        }

        if (props.initialValues && props.initialValues.length > 0) {
            const removedFiles = props.initialValues.filter(initialFile =>
                !currentExistingFiles
                    .map(item => item.id)
                    .includes(initialFile.id)
            );
            setRemovedFiles(removedFiles)
        }
        
    }, [files, props.existingFilesFieldName, props.initialValues, props.name, setFieldValue]);

    useEffect(() => {
        if (props.onRemovedFiles !== undefined) {
            props.onRemovedFiles(removedFiles); 
        }
    }, [removedFiles]);

    return (
        <>
            <label htmlFor={props.name} className={styles.uploadLabel}>{props.label}</label>
            {validateExtension 
                ? <input type='file' multiple onChange={handleFileChange} id='files' className={styles.hidden} accept={acceptedExtensions} />
                : <input type='file' multiple onChange={handleFileChange} id='files' className={styles.hidden} />}

            <label htmlFor='files' className={styles.fakeFileButton}>{intl.formatMessage({ id: Lx.Cost.UPLOAD_FILES })}</label>
            {props.hint ? (<div className={styles.hint}>{props.hint}</div>) : null}
            {meta.error ? (<div className={styles.error}>{meta.error}</div>) : null}

            {files && [...files].map((item, index) => {
                const isLocal = item.source === 'local';
                const fileExtension = isLocal
                    ? ''
                    : getFileExtension((item.file as FileMetadata).extension);
                const fileName = isLocal
                    ? (item.file as File).name
                    : (item.file as FileMetadata).name;

                const fullFileName = `${fileName}${(fileExtension !== '' ? '.'+ fileExtension : '')}`;
                return (<div key={index} className={styles.fileRow}>
                    <DownloadButton
                        fileName={fullFileName}
                        downloadUrl={!isLocal ? (item.file as FileMetadata).fileDownloadUrl : undefined}
                        downloadFileName={fileName}
                        truncate
                        preserveExtensionOnTruncate
                        wrapperClassName={styles.downloadButtonWrapper}
                        className={styles.downloadButton}
                    />
                    <Button 
                        type='button'
                        onClick={() => removeFile(index)}
                        className={styles.deleteButton}>
                        <SVGS.DeleteIcon />
                    </Button>
                </div>
                )})}
                
        </>
    );
};

export default MultipleFileUploader;
